import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
//import { examSetupApi, setupError } from "../examSetupApi";
import axios from "axios";
const outlinedLabelProps = {
  shrink: true,
  sx: {
    backgroundColor: "#fff",
    color: "#344767",
    fontSize: "0.95rem",
    fontWeight: 500,
    px: 0.75,
    "&.Mui-focused": { color: "#1976d2" },
  },
};

const filterLabels = {
  collegeId: "College ID",
  academicYear: "Academic Year",
  program: "Program",
  yearNumber: "Year Number",
  semesterNumber: "Semester Number",
};
function examSetupApi() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  if (!user?.client_id || !user?.college_id)
    throw new Error("Select a college before managing exam setup");
  const base = `${process.env.REACT_APP_API_BASE_URL}/api/exam-setup/${user.client_id}/${user.college_id}`;
  const token = sessionStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return {
    get: async (path, params) => (await axios.get(`${base}${path}`, { params, headers })).data.data,
    post: async (path, data) => (await axios.post(`${base}${path}`, data, { headers })).data.data,
    put: async (path, data) => (await axios.put(`${base}${path}`, data, { headers })).data.data,
    delete: async (path) => (await axios.delete(`${base}${path}`, { headers })).data,
  };
}

function setupError(error) {
  return error.response?.data?.message || error.message || "Unable to save exam setup";
}
export default function RegularExamFee() {
  const [years, setYears] = useState([]),
    [options, setOptions] = useState({ programs: [], mappings: [] });
  const [missing, setMissing] = useState([]),
    [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    acyear_id: "",
    program_id: "",
    year_number: "",
    semester_number: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [visibleMissingYears, setVisibleMissingYears] = useState({});
  const [missingWarningIndex, setMissingWarningIndex] = useState(0);
  const [showMissingWarning, setShowMissingWarning] = useState(true);
  const [fees, setFees] = useState({}),
    [message, setMessage] = useState(null),
    [busy, setBusy] = useState(false),
    [showMissing, setShowMissing] = useState(false);
  const api = examSetupApi();
  const loadSetup = async () => {
    try {
      const [yearRows, optionRows, missingRows] = await Promise.all([
        api.get("/academic-years"),
        api.get("/fee-options"),
        api.get("/missing-regular-fees"),
      ]);
      setYears(yearRows);
      setOptions(optionRows);
      setMissing(missingRows);
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    }
  };
  useEffect(() => {
    loadSetup();
  }, []);
  const mappings = useMemo(
    () =>
      options.mappings.filter(
        (mapping) => String(mapping.program_id) === String(filters.program_id)
      ),
    [options, filters.program_id]
  );
  const yearNumbers = useMemo(
    () => [...new Set(mappings.map((mapping) => mapping.year_number))],
    [mappings]
  );
  const semesters = useMemo(
    () => [
      ...new Set(
        mappings
          .filter((mapping) => String(mapping.year_number) === String(filters.year_number))
          .map((mapping) => mapping.semester_number)
      ),
    ],
    [mappings, filters.year_number]
  );
  const setFilter = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === "program_id" ? { year_number: "", semester_number: "" } : {}),
      ...(name === "year_number" ? { semester_number: "" } : {}),
    }));
    setRows([]);
    setFees({});
  };
  const ready = Object.values(filters).every(Boolean);
  useEffect(() => {
    if (!ready) return;
    let mounted = true;
    api
      .get("/regular-fees", filters)
      .then((data) => {
        if (!mounted) return;
        setRows(data);
        setFees(Object.fromEntries(data.map((row) => [row.branch_id, row.fee_amount ?? ""])));
      })
      .catch((error) => {
        if (mounted) setMessage({ type: "error", text: setupError(error) });
      });
    return () => {
      mounted = false;
    };
  }, [filters.acyear_id, filters.program_id, filters.year_number, filters.semester_number]);
  const save = async (branchIds) => {
    const entries = branchIds
      .filter((id) => fees[id] !== "" && fees[id] !== null && fees[id] !== undefined)
      .map((id) => ({ branch_id: id, fee_amount: fees[id] }));
    if (!entries.length) {
      setMessage({ type: "warning", text: "Enter a fee for at least one branch" });
      return;
    }
    setBusy(true);
    try {
      await api.put("/regular-fees", { ...filters, entries });
      const [feeRows, missingRows] = await Promise.all([
        api.get("/regular-fees", filters),
        api.get("/missing-regular-fees"),
      ]);
      setRows(feeRows);
      setMissing(missingRows);
      setMessage({
        type: "success",
        text: `${entries.length} branch fee${entries.length === 1 ? "" : "s"} saved`,
      });
    } catch (error) {
      setMessage({ type: "error", text: setupError(error) });
    } finally {
      setBusy(false);
    }
  };
  const missingYears = [
    ...new Map(missing.map((item) => [item.acyear_id, item.academic_year])).entries(),
  ];
  const selectedMissing = missing.filter(
    (item) =>
      String(item.acyear_id) === String(filters.acyear_id) &&
      String(item.program_id) === String(filters.program_id) &&
      String(item.year_number) === String(filters.year_number) &&
      String(item.semester_number) === String(filters.semester_number)
  );
  const jump = (item) =>
    setFilters({
      acyear_id: String(item.acyear_id),
      program_id: String(item.program_id),
      year_number: String(item.year_number),
      semester_number: String(item.semester_number),
    });
  const columns = [
    {
      field: "serialNo",
      headerName: "S. No.",
      width: 80,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        // params.api.getRowIndex(params.id) returns the index in the current page
        return params.api ? params.api.getRowIndex(params.id) + 1 : "";
      },
    },
    { field: "branch_name", headerName: "Branch", minWidth: 260, flex: 1.5 },
    {
      field: "fee_amount",
      headerName: "Regular Exam Fee",
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <TextField
          size="large"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={fees[params.row.branch_id] ?? ""}
          onChange={(event) =>
            setFees((current) => ({ ...current, [params.row.branch_id]: event.target.value }))
          }
          placeholder="₹"
          sx={{ width: 160 }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.7,
      valueGetter: (params) => (params.row.regular_exam_fee_id ? "Configured" : "Missing"),
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 140,
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="large"
          disabled={busy || fees[params.row.branch_id] === ""}
          onClick={() => save([params.row.branch_id])}
        >
          {params.row.regular_exam_fee_id ? "Update" : "Save"}
        </Button>
      ),
    },
  ];
  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        SETUP REGULAR EXAM FEE
      </Typography>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setMessage(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 7, mr: 2 }}
      >
        <Alert
          onClose={() => setMessage(null)}
          severity={message?.type || "info"}
          variant="filled"
          elevation={6}
          sx={{
            width: "100%",
            minWidth: 300,
            fontSize: "0.95rem",
            fontWeight: 500,
            alignItems: "center",
            "& .MuiAlert-icon": {
              fontSize: 26,
            },
          }}
        >
          {message?.text}
        </Alert>
      </Snackbar>
      {missingYears.length > 0 && (
        <Snackbar
          key={missingWarningIndex}
          open={showMissingWarning && missingWarningIndex < missingYears.length}
          autoHideDuration={5000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;

            if (missingWarningIndex < missingYears.length - 1) {
              setMissingWarningIndex((prev) => prev + 1);
            } else {
              setShowMissingWarning(false);
            }
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{ mt: 3, mr: 2 }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => {
              if (missingWarningIndex < missingYears.length - 1) {
                setMissingWarningIndex((prev) => prev + 1);
              } else {
                setShowMissingWarning(false);
              }
            }}
            sx={{ width: "100%" }}
          >
            Regular Exam Fee is not fully configured for Academic Year{" "}
            {missingYears[missingWarningIndex]?.[1]}. Please complete the Exam Fee Setup.
          </Alert>
        </Snackbar>
      )}
      {missing.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">
              Missing required combinations: {missing.length}
            </Typography>
            <Button size="large" onClick={() => setShowMissing((current) => !current)}>
              {showMissing ? "Hide Details" : "View Details"}
            </Button>
          </Stack>
          {showMissing && (
            <Box sx={{ mt: 1, maxHeight: 150, overflowY: "auto" }}>
              {missing.map((item) => (
                <Button
                  key={`${item.acyear_id}-${item.program_id}-${item.year_number}-${item.semester_number}-${item.branch_id}`}
                  size="small"
                  onClick={() => jump(item)}
                  sx={{ m: 0.25, textTransform: "none" }}
                >
                  {item.academic_year} · {item.program_name} · Year {item.year_number} · Semester{" "}
                  {item.semester_number} · {item.branch_name}
                </Button>
              ))}
            </Box>
          )}
        </Paper>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            select
            label={filterLabels.academicYear}
            size="small"
            required
            InputLabelProps={outlinedLabelProps}
            fullWidth
            value={filters.acyear_id}
            SelectProps={{ native: true }}
            onChange={(e) => setFilter("acyear_id", e.target.value)}
          >
            <option value="">Select Academic Year</option>
            {years.map((x) => (
              <option key={x.acyear_id} value={x.acyear_id}>
                {x.academic_year}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label={filterLabels.program}
            InputLabelProps={outlinedLabelProps}
            size="small"
            required
            fullWidth
            value={filters.program_id}
            SelectProps={{ native: true }}
            onChange={(e) => setFilter("program_id", e.target.value)}
          >
            <option value="">Select Program</option>
            {options.programs.map((program) => (
              <option key={program.program_id} value={program.program_id}>
                {program.program_name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label={filterLabels.yearNumber}
            InputLabelProps={outlinedLabelProps}
            size="small"
            required
            fullWidth
            value={filters.year_number}
            SelectProps={{ native: true }}
            onChange={(e) => setFilter("year_number", e.target.value)}
            disabled={!filters.program_id}
          >
            <option value="">Select Study Year</option>
            {yearNumbers.map((number) => (
              <option key={number} value={number}>
                {number}
                {number === 1 ? "st" : number === 2 ? "nd" : number === 3 ? "rd" : "th"} Year
              </option>
            ))}
          </TextField>
          <TextField
            select
            label={filterLabels.semesterNumber}
            InputLabelProps={outlinedLabelProps}
            size="small"
            required
            fullWidth
            value={filters.semester_number}
            SelectProps={{ native: true }}
            onChange={(e) => setFilter("semester_number", e.target.value)}
            disabled={!filters.year_number}
          >
            <option value="">Select Study Year</option>
            {semesters.map((number) => (
              <option key={number} value={number}>
                Semester {number}
              </option>
            ))}
          </TextField>
        </Stack>
      </Paper>
      {ready && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ height: 430, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.branch_id}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
              rowHeight={60}
            />
          </Box>
          <Box p={2}>
            <Button
              variant="contained"
              disabled={busy || !rows.length}
              onClick={() => save(rows.map((row) => row.branch_id))}
              sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
            >
              Bulk Save / Update
            </Button>
            {selectedMissing.length > 0 && (
              <Typography color="warning.main" variant="body2" sx={{ mt: 1 }}>
                {selectedMissing.length} branch fees are missing for this selection.
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
